'use client'
import { SyntheticEvent } from 'react'
import { memo, useRef, useEffect, useCallback } from 'react'
import type { Dispatch, SetStateAction, FC } from 'react'

type Props = {
  options?: {
    autoPlay?: boolean
    muted?: boolean
    loop?: boolean
  }
  data: {
    alt?: string
    contentType?: 'video/mp4' | 'video/webm'
    size?: { width: number, height: number }
    src: string
  }
  className?: string
  setVideoReady?: Dispatch<SetStateAction<boolean>> | (() => void)
  play?: boolean
}

const handleVideoError = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
  console.error('Video loading error:', event)
  // Try to provide helpful error information
  const video = event.currentTarget
  if (video?.error) {
    const errorCode = video.error.code
    const errorMessages = {
      1: 'MEDIA_ERR_ABORTED: Video loading was aborted',
      2: 'MEDIA_ERR_NETWORK: Network error occurred while loading video',
      3: 'MEDIA_ERR_DECODE: Video decoding error',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED: Video format not supported'
    }
    console.error(errorMessages[errorCode as keyof typeof errorMessages] || 'Unknown video error')
  }
}

export const Video: FC<Props> = memo(({
  data,
  className,
  options = { autoPlay: false, muted: true, loop: true },
  play,
  setVideoReady
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  // Memoize the video ready handler to prevent unnecessary re-renders
  const handleVideoReady = useCallback(() => {
    if (options.autoPlay) setVideoReady?.(true)
  }, [options.autoPlay, setVideoReady])

  // Control video playback based on play prop with error handling
  useEffect(() => {
    if (typeof play !== 'boolean') return

    const playVideo = async () => {
      const video = videoRef.current

      try {
        if (play) {
          await video.play()
        } else {
          video.pause()
        }
      } catch (error) {
        // Handle autoplay restrictions or other playback errors
        console.warn('Video playback error:', error)
      }
    }

    playVideo()
  }, [play])

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    const video = videoRef.current
    return () => {
      if (video) {
        video.pause()
        // video.src = ''
        video.load()
      }
    }
  }, [])

  return (
    <video
      ref={videoRef}
      muted={options.muted}
      autoPlay={options.autoPlay}
      loop={options.loop}
      onCanPlay={handleVideoReady}
      onLoadedData={handleVideoReady}
      onError={handleVideoError}
      preload='auto'
      playsInline // Better mobile performance
      webkit-playsinline='true' // iOS support
      // disablePictureInPicture
      {...(className && { className })}
    >
      <source src={data.src} type={data.contentType} />
      Your browser does not support the video tag.
    </video>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function for memo optimization
  return (
    prevProps.data.src === nextProps.data.src &&
    prevProps.play === nextProps.play &&
    prevProps.className === nextProps.className &&
    prevProps.options?.autoPlay === nextProps.options?.autoPlay &&
    prevProps.options?.muted === nextProps.options?.muted &&
    prevProps.options?.loop === nextProps.options?.loop
  )
})

Video.displayName = 'Video'
